import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const [formType, setFormType] = useState("contact"); // "contact" or "brochure"
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    nom: "",
    email: "",
    telephone: "",
    entreprise: "",
    message: "",
    nombre_lieux: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        form_type: formType
      });

      toast.success(
        formType === "brochure"
          ? "Demande envoyée ! Vous recevrez la brochure par email."
          : "Message envoyé ! Nous vous recontactons rapidement."
      );

      setFormData({
        nom: "",
        email: "",
        telephone: "",
        entreprise: "",
        message: "",
        nombre_lieux: ""
      });
    } catch (error) {
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-[#F9F6F0] py-24 md:py-32 lg:py-40"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left Column - Content */}
          <div>
            <p
              data-testid="contact-label"
              className="font-body text-sm tracking-[0.2em] uppercase text-[#B89F7C] mb-6"
            >
              Contactez-nous
            </p>
            <h2
              data-testid="contact-headline"
              className="font-heading text-4xl sm:text-5xl font-light tracking-tight leading-tight text-[#0A0A0A] mb-8"
            >
              Transformez chaque spray
              <br />
              <span className="italic">en opportunité client</span>
            </h2>
            <p className="font-body text-lg font-light leading-relaxed text-[#525252] mb-12">
              Prêt à donner une nouvelle dimension à votre marque ? Demandez
              une présentation ou recevez notre brochure détaillée.
            </p>

            {/* Form Type Toggle */}
            <div className="flex gap-4 mb-8">
              <button
                type="button"
                onClick={() => setFormType("contact")}
                data-testid="toggle-contact"
                className={`px-6 py-3 text-sm tracking-widest uppercase font-medium transition-colors duration-300 ${
                  formType === "contact"
                    ? "bg-[#0A0A0A] text-white"
                    : "bg-transparent border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#FAFAFA]"
                }`}
              >
                Réserver un appel
              </button>
              <button
                type="button"
                onClick={() => setFormType("brochure")}
                data-testid="toggle-brochure"
                className={`px-6 py-3 text-sm tracking-widest uppercase font-medium transition-colors duration-300 ${
                  formType === "brochure"
                    ? "bg-[#0A0A0A] text-white"
                    : "bg-transparent border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#FAFAFA]"
                }`}
              >
                Recevoir la brochure
              </button>
            </div>
          </div>

          {/* Right Column - Form */}
          <div>
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="space-y-6"
            >
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="nom"
                    className="font-body text-sm tracking-wide text-[#525252] mb-2 block"
                  >
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    id="nom"
                    name="nom"
                    required
                    value={formData.nom}
                    onChange={handleChange}
                    data-testid="input-nom"
                    className="w-full bg-transparent border-b border-black/20 pb-3 focus:border-[#B89F7C] focus:outline-none text-lg transition-colors placeholder:text-gray-400"
                    placeholder="Jean Dupont"
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="font-body text-sm tracking-wide text-[#525252] mb-2 block"
                  >
                    Email professionnel *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    data-testid="input-email"
                    className="w-full bg-transparent border-b border-black/20 pb-3 focus:border-[#B89F7C] focus:outline-none text-lg transition-colors placeholder:text-gray-400"
                    placeholder="jean@marque.com"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="telephone"
                    className="font-body text-sm tracking-wide text-[#525252] mb-2 block"
                  >
                    Téléphone *
                  </label>
                  <input
                    type="tel"
                    id="telephone"
                    name="telephone"
                    required
                    value={formData.telephone}
                    onChange={handleChange}
                    data-testid="input-telephone"
                    className="w-full bg-transparent border-b border-black/20 pb-3 focus:border-[#B89F7C] focus:outline-none text-lg transition-colors placeholder:text-gray-400"
                    placeholder="+33 6 00 00 00 00"
                  />
                </div>
                <div>
                  <label
                    htmlFor="entreprise"
                    className="font-body text-sm tracking-wide text-[#525252] mb-2 block"
                  >
                    Entreprise / Marque *
                  </label>
                  <input
                    type="text"
                    id="entreprise"
                    name="entreprise"
                    required
                    value={formData.entreprise}
                    onChange={handleChange}
                    data-testid="input-entreprise"
                    className="w-full bg-transparent border-b border-black/20 pb-3 focus:border-[#B89F7C] focus:outline-none text-lg transition-colors placeholder:text-gray-400"
                    placeholder="Maison de Parfum"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="nombre_lieux"
                  className="font-body text-sm tracking-wide text-[#525252] mb-2 block"
                >
                  Nombre de lieux envisagés
                </label>
                <select
                  id="nombre_lieux"
                  name="nombre_lieux"
                  value={formData.nombre_lieux}
                  onChange={handleChange}
                  data-testid="select-nombre-lieux"
                  className="w-full bg-transparent border-b border-black/20 pb-3 focus:border-[#B89F7C] focus:outline-none text-lg transition-colors cursor-pointer"
                >
                  <option value="">Sélectionnez une option</option>
                  <option value="1-3">1 à 3 lieux</option>
                  <option value="4-9">4 à 9 lieux</option>
                  <option value="10+">10 lieux ou plus</option>
                  <option value="unsure">Je ne sais pas encore</option>
                </select>
              </div>

              {formType === "contact" && (
                <div>
                  <label
                    htmlFor="message"
                    className="font-body text-sm tracking-wide text-[#525252] mb-2 block"
                  >
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    data-testid="input-message"
                    className="w-full bg-transparent border-b border-black/20 pb-3 focus:border-[#B89F7C] focus:outline-none text-lg transition-colors placeholder:text-gray-400 resize-none"
                    placeholder="Parlez-nous de votre projet..."
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                data-testid="contact-form-submit"
                className="w-full bg-[#0A0A0A] text-white hover:bg-[#B89F7C] transition-colors duration-300 px-8 py-4 text-sm tracking-widest uppercase font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {formType === "brochure"
                  ? "Recevoir la brochure"
                  : "Demander une présentation"}
              </button>

              <p className="font-body text-xs text-[#525252] text-center">
                En soumettant ce formulaire, vous acceptez d'être contacté par notre équipe.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
